/**
 * Utility functions for web analytics tracking
 */

import { URLS } from '../config/env';
import { DEFAULT_DF_APP_ID, PROFILE_BUILDER_APP_ID, PROFILE_BUILDER_ENABLED } from '../config/profile-builder';
import { trackLeadFormFieldInvalid } from '../lib/lead-form-analytics';

const generateUuid = () => {
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g,
    (c: string) => (parseInt(c) ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> parseInt(c) / 4).toString(16)
  );
};

/**
 * Resolve the analytics application_id from the LD variant marker in the DOM.
 * Variant B pages stamp data-ld-variant="B" on their wrapper; everything else
 * (variant A and non-test pages) falls back to the legacy app id.
 */
const getApplicationId = (): string => {
  if (!PROFILE_BUILDER_ENABLED || typeof document === 'undefined') return DEFAULT_DF_APP_ID;
  const variant = document.querySelector('[data-ld-variant]')?.getAttribute('data-ld-variant');
  return variant === 'B' ? PROFILE_BUILDER_APP_ID : DEFAULT_DF_APP_ID;
};

interface WebAnalyticsPayload {
  page: {
    url: string;
    visitorId: string;
    divisionId: string;
    userAgent: string;
  };
  domEventData: {
    target: string;
    classes?: string[];
    text?: string;
    data?: {
      leadformVersion?: string;
      leadformAction?: string;
      [key: string]: any;
    };
  };
  customEventData: {
    action: string;
    category: string;
    label?: string;
  };
  pageRequestId: string;
  leadFormEventData?: {
    id?: string;
    panel?: string;
  };
}

// ---------------------------------------------------------------------------
// Cookie helpers (GA, Meta)
// ---------------------------------------------------------------------------

const getGAClientId = (): string | undefined => {
  try {
    const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]*)/);
    return match?.[1] || undefined;
  } catch {
    return undefined;
  }
};

const getGASessionId = (): string | undefined => {
  try {
    const match = document.cookie.match(/(?:^|;\s*)_ga_[A-Z0-9]+=GS\d\.\d\.(\d+)/);
    return match?.[1] || undefined;
  } catch {
    return undefined;
  }
};

const getFbp = (): string | undefined => {
  try {
    const match = document.cookie.match(/(?:^|;\s*)_fbp=([^;]*)/);
    return match?.[1] || undefined;
  } catch {
    return undefined;
  }
};

// ---------------------------------------------------------------------------
// Event type mapper
// ---------------------------------------------------------------------------

const mapToEventType = (customEventData: { action: string; category: string }): string => {
  const { action, category } = customEventData;

  if (action === 'loaded' && category === 'visits') return 'page_view';
  if (category === 'form_wizard_submit') return 'lead_creation';
  if (category === 'form_wizard_panel') return 'form_step_complete';
  if (category === 'form_wizard_error') return 'form_field_invalid';
  if (category === 'form_wizard_start') return 'form_start';
  if (category === 'form_wizard_abandon') return 'form_abandon';
  if (action === 'click' && category === 'visits') return 'form_interaction';

  return `${category}_${action}`;
};

// ---------------------------------------------------------------------------
// Core send
// ---------------------------------------------------------------------------

const sendWebAnalyticsEvent = async (payload: Partial<WebAnalyticsPayload>, useKeepAlive = false) => {
  if (typeof window === 'undefined') return;

  const defaultPayload: WebAnalyticsPayload = {
    page: {
      url: window.location.href,
      visitorId: sessionStorage.getItem('visitor_id') || localStorage.getItem('visitor_id') || `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      divisionId: 'VT::DM',
      userAgent: navigator.userAgent
    },
    domEventData: {
      target: 'page'
    },
    customEventData: {
      action: 'loaded',
      category: 'visits'
    },
    pageRequestId: `ngx-${generateUuid()}`
  };

  const finalPayload = { ...defaultPayload, ...payload };

  // Pipeline 1: Lead Connector event tracking (fire-and-forget)
  try {
    const eventType = mapToEventType(finalPayload.customEventData);

    fetch('/df/api/track-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: useKeepAlive,
      body: JSON.stringify({
        origin: 'paid-leadform',
        event_id: finalPayload.pageRequestId,
        data: {
          event_type: eventType,
          timestamp: Date.now(),
          visitor_id: finalPayload.page.visitorId,
          ga_client_id: getGAClientId(),
          ga_session_id: getGASessionId(),
          fbp: getFbp(),
          page_url: finalPayload.page.url,
          page_title: typeof document !== 'undefined' ? document.title : undefined,
          page_referrer: typeof document !== 'undefined' ? document.referrer : undefined,
          application_id: getApplicationId(),
          event_action: finalPayload.customEventData.action,
          event_category: finalPayload.customEventData.category,
          event_label: finalPayload.customEventData.label,
          dom_target: finalPayload.domEventData.target,
          panel: finalPayload.leadFormEventData?.panel,
          event_data: finalPayload.domEventData.data || undefined,
        },
      }),
    }).catch(() => {});
  } catch {
    /* fire-and-forget */
  }

  // Pipeline 2: VT events API (existing)
  try {
    const fetchOptions: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-vt-applicationid': getApplicationId()
      },
      body: JSON.stringify(finalPayload)
    };
    
    if (useKeepAlive) {
      fetchOptions.keepalive = true;
    }
    
    const response = await fetch(URLS.EVENTS_API, fetchOptions);
    return response;
  } catch {
    // Silently fail in production
  }
};

/**
 * Track page load event
 */
export const trackPageLoad = async () => {
  // Send the page load event
  sendWebAnalyticsEvent({
    customEventData: {
      action: 'loaded',
      category: 'visits'
    }
  });
};

/**
 * Track form interaction event
 */
export const trackFormInteraction = (panelId: string, buttonText: string) => {
  sendWebAnalyticsEvent({
    domEventData: {
      target: 'LABEL',
      classes: [],
      text: buttonText
    },
    customEventData: {
      action: 'click',
      category: 'visits'
    },
    leadFormEventData: {
      id: '',
      panel: panelId
    }
  });
};

/**
 * Track continue button click in multiselect panel
 */
export const trackContinueButtonClick = (panelId: string) => {
  sendWebAnalyticsEvent({
    domEventData: {
      target: 'BUTTON',
      classes: [],
      text: 'Continue'
    },
    customEventData: {
      action: 'click',
      category: 'visits'
    },
    leadFormEventData: {
      id: '',
      panel: panelId
    }
  });
};

/**
 * Track checkbox click for terms of use agreement
 */
export const trackCheckboxClick = (panelId: string, checkboxText: string) => {
  sendWebAnalyticsEvent({
    domEventData: {
      target: 'LABEL',
      classes: [],
      text: checkboxText
    },
    customEventData: {
      action: 'click',
      category: 'visits'
    },
    leadFormEventData: {
      id: '',
      panel: panelId
    }
  });
};

/**
 * Track successful lead creation
 */
export const trackLeadCreationSuccess = (leadformVersion: string) => {
  sendWebAnalyticsEvent({
    domEventData: {
      target: 'lead_creation',
      data: {
        leadformVersion,
        leadformAction: 'lead_creation'
      }
    },
    customEventData: {
      action: 'lead-form',
      category: 'form_wizard_submit',
      label: 'lead_creation'
    }
  }, true); // Use keepalive for lead creation event
};

/**
 * Track form start (first interaction or render)
 */
export const trackFormStart = () => {
  sendWebAnalyticsEvent({
    domEventData: {
      target: 'lead_form_start',
    },
    customEventData: {
      action: 'lead-form',
      category: 'form_wizard_start',
      label: 'lead_form_start'
    }
  });
};

/**
 * Track form abandon (before unload). Uses keepalive to ensure delivery.
 */
export const trackFormAbandon = (panelId?: string) => {
  sendWebAnalyticsEvent({
    domEventData: {
      target: 'lead_form_abandon',
      data: {
        panel: panelId || ''
      }
    },
    customEventData: {
      action: 'lead-form',
      category: 'form_wizard_abandon',
      label: panelId || 'unknown'
    }
  }, true);
};

/**
 * Push lead_created event to dataLayer
 */
export const pushLeadCreatedToDataLayer = (leadId?: string) => {
  if (typeof window === 'undefined') return;
  
  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];
  
  // Push the lead_created event with relevant data
  window.dataLayer.push({
    event: 'lead_created',
    leadData: {
      leadId: leadId || 'unknown',
      timestamp: new Date().toISOString(),
      source: window.location.href,
      visitorId: sessionStorage.getItem('visitor_id') || localStorage.getItem('visitor_id') || null
    }
  });
};

/**
 * Track successful completion of a form step
 */
export const trackFormStepComplete = (panelId: string, leadformVersion: string = "") => {
  const eventName = `formWizard_${panelId.replace(/-/g, '_')}_complete`;
  const leadformActionName = `formWizard.${panelId.replace(/-/g, '_')}.complete`;
  
  sendWebAnalyticsEvent({
    domEventData: {
      target: eventName,
      data: {
        leadformVersion: "",
        leadformAction: leadformActionName
      }
    },
    customEventData: {
      action: 'lead-form',
      category: 'form_wizard_panel',
      label: eventName
    }
  }, false);
};

/**
 * Track successful after-hours callback scheduling
 */
export const trackScheduleCallbackComplete = (
  leadformVersion: string,
  scheduledTimeLabel: string,
) => {
  const eventName = 'formWizard_schedule_callback_complete';
  const leadformActionName = 'formWizard.schedule_callback.complete';

  sendWebAnalyticsEvent({
    domEventData: {
      target: eventName,
      data: {
        leadformVersion,
        leadformAction: leadformActionName,
        scheduledTime: scheduledTimeLabel,
      },
    },
    customEventData: {
      action: 'lead-form',
      category: 'form_wizard_panel',
      label: eventName,
    },
  }, true);
};

/**
 * Track invalid field event (on submit attempt) per spec
 * customEventData.action: 'lead-form'
 * customEventData.category: 'form_wizard_error'
 * customEventData.label: e.g., 'invalid_email', 'invalid_phone_number'
 * domEventData.data.leadformAction: 'formWizard.error'
 * domEventData.target: e.g., 'formWizard_error_invalid_phone_number'
 */
export const trackFormFieldInvalid = (panelId: string, invalidLabel: string) => {
  const normalized = invalidLabel.replace(/\s+/g, '_');
  const eventTarget = `formWizard_error_invalid_${normalized}`;
  // The same error in the PostHog lead form funnel (a no-op outside a tracked form).
  trackLeadFormFieldInvalid(panelId, `invalid_${normalized}`);

  sendWebAnalyticsEvent({
    domEventData: {
      target: eventTarget,
      data: {
        leadformAction: 'formWizard.error'
      }
    },
    customEventData: {
      action: 'lead-form',
      category: 'form_wizard_error',
      label: `invalid_${normalized}`
    },
    leadFormEventData: {
      id: '',
      panel: panelId
    }
  });
};